import { useMe } from '../../../organisation/useMe';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { screeningFlowsApi } from '../../../apis';
import { InternalApiEntityType, InternalApiScreeningFlow } from '../../../api-internal';

export const useQueryScreeningFlows = (entityType: InternalApiEntityType | undefined) => {
  const { currentOrganisation } = useMe();

  return useQuery({
    queryKey: ['screeningFlows', entityType, currentOrganisation?.id],
    queryFn: async () => await screeningFlowsApi.findScreeningFlows({ entityType: entityType! }),
    enabled: !!entityType && !!currentOrganisation?.id,
  });
};

export const useQueryScreeningFlow = (flowId: string | undefined) => {
  const { currentOrganisation } = useMe();

  return useQuery({
    queryKey: ['screeningFlow', flowId, currentOrganisation?.id],
    queryFn: async () => {
      const exists =
        ((await screeningFlowsApi.checkActiveScreeningFlowExists({ flowId: flowId! })) as unknown as string) === 'true';
      if (!exists) {
        return null;
      }
      return await screeningFlowsApi.getScreeningFlow({ flowId: flowId! });
    },
    enabled: !!flowId && !!currentOrganisation?.id,
  });
};

export const useCreateScreeningFlow = (entityType: InternalApiEntityType) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: (flow: InternalApiScreeningFlow) =>
      screeningFlowsApi.createScreeningFlow({ internalApiScreeningFlow: flow }).then((result) => result),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['screeningFlows', entityType, currentOrganisation?.id],
      });
    },
  });
};

export const useUpdateScreeningFlow = (entityType: InternalApiEntityType) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: ({ flowId, flow }: { flowId: string; flow: InternalApiScreeningFlow }) =>
      screeningFlowsApi
        .updateScreeningFlow({ flowId: flowId, internalApiScreeningFlow: flow })
        .then((result) => result),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['screeningFlows', entityType, currentOrganisation?.id],
      });
    },
  });
};

export const useDeleteScreeningFlow = (entityType: InternalApiEntityType) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: (flowId: string) => screeningFlowsApi.deleteFlow({ flowId }).then((_) => flowId),
    onSuccess: async (flowId) => {
      await queryClient.invalidateQueries({
        queryKey: ['screeningFlows', entityType, currentOrganisation?.id],
      });

      queryClient.setQueryData(
        ['screeningFlows', entityType, currentOrganisation?.id],
        (oldData: InternalApiScreeningFlow[] | undefined) => {
          return oldData ? [...oldData.filter((it) => it.id !== flowId)] : [];
        }
      );
    },
  });
};
